// WCAG 2.2 contrast mathematics.
//
// Pure functions, no dependencies — this is the measuring instrument the token
// tests use, so it must be trivially auditable. WCAG 2.2 inherits 2.1's contrast
// definitions unchanged (SC 1.4.3, 1.4.11); the formulas below are transcribed
// from the specification rather than approximated.
//
// Accessibility is not decoration in this product: the plan expects older adults,
// people with low vision, low digital confidence and people reading under stress
// (section 24). NFR-ACC-001 requires WCAG 2.2 AA for core flows.

/** An sRGB colour as three 0-255 channels. */
export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** WCAG AA thresholds. Values are ratios, e.g. 4.5 means 4.5:1. */
export const AA = {
  /** SC 1.4.3 — normal-size text. */
  normalText: 4.5,
  /** SC 1.4.3 — large text (>=24px, or >=18.66px bold). */
  largeText: 3,
  /** SC 1.4.11 — user-interface components and graphical objects. */
  nonText: 3,
} as const;

/** Parse `#rgb` or `#rrggbb`. Throws on anything else — a malformed token is a bug, not a fallback. */
export function parseHex(hex: string): Rgb {
  const value = hex.trim().replace(/^#/, "");

  const expanded =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    throw new Error(`Not a hex colour: '${hex}'. Tokens must be #rgb or #rrggbb.`);
  }

  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

/**
 * Relative luminance, per WCAG 2.x.
 *
 * https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
 */
export function relativeLuminance(colour: Rgb): number {
  const channel = (raw: number): number => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(colour.r) + 0.7152 * channel(colour.g) + 0.0722 * channel(colour.b);
}

/**
 * Contrast ratio between two colours, 1..21.
 *
 * https://www.w3.org/TR/WCAG22/#dfn-contrast-ratio
 * Order-independent: contrast(a, b) === contrast(b, a).
 */
export function contrastRatio(foreground: string | Rgb, background: string | Rgb): number {
  const fg = typeof foreground === "string" ? parseHex(foreground) : foreground;
  const bg = typeof background === "string" ? parseHex(background) : background;

  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1];

  return (lighter + 0.05) / (darker + 0.05);
}

/** Round to 2dp for reporting. Never use this before comparing against a threshold. */
export function round2(ratio: number): number {
  return Math.round(ratio * 100) / 100;
}

/**
 * Perceived lightness in greyscale terms, 0..1.
 *
 * Used to check the plan's section 24.6 rule that states stay distinguishable with
 * colour removed — for colour-blind readers, monochrome printing (the product has a
 * print surface), and forced-colours modes.
 */
export function greyscaleLightness(colour: string | Rgb): number {
  return relativeLuminance(typeof colour === "string" ? parseHex(colour) : colour);
}
